import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { AriaSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

/**
 * The dashboard's tables, in the style of the Tools page: a bordered box that
 * scrolls sideways on its own when the table is wider than its room (never the
 * page), small text, dense rows with a line between them, a header row in a
 * light grey tint and group rows in De Ceuster's light blue tint. A row leads
 * with a small line icon, a title and a muted summary line (`<TableItem>`),
 * the state sits right (`<StateText>`, a chip or a switch), and a row that is
 * off, blocked or unused is muted. On a phone a table takes its phone layout
 * (`NARROW`): the columns in between leave and their facts move under the
 * title, so it fits the screen. Every column of a table sorts: its header is a
 * `<SortableHeadCell>`, driven by `useTableSort` (`@/lib/table-sort`).
 *
 *     <DataTable label="Gebruikers">
 *       <TableHead>
 *         <TableHeadCell>Gebruiker</TableHeadCell>
 *         <TableHeadCell narrow="hide">Rol</TableHeadCell>
 *         <TableHeadCell align="right">Status</TableHeadCell>
 *       </TableHead>
 *       <tbody>
 *         <TableRow>
 *           <TableCell>
 *             <TableItem icon={…} title="Gerd" summary="gerd@…" facts={<Chip>Beheerder</Chip>} />
 *           </TableCell>
 *           <TableCell narrow="hide"><Chip>Beheerder</Chip></TableCell>
 *           <TableCell align="right"><StateText>Uit</StateText></TableCell>
 *         </TableRow>
 *       </tbody>
 *     </DataTable>
 */

type Align = "left" | "center" | "right";

const ALIGN: Record<Align, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/**
 * A table's phone layout. Below 640 px of its own box (a container query, so
 * the window and the sidebar both count) the columns between the first and
 * the last leave, and the first cell lists what they said under the row's
 * title (`<TableItem facts>`): each row reads top to bottom, with its state or
 * its actions on the right, and the table never scrolls sideways on a phone.
 *
 * From a 1024 px window up, beside the sidebar, every table's box is wider
 * (at least 680 px), so it keeps all its columns. From 640 to 1023 px the box
 * is the window less the 72 px rail and the page's margins, 514 to 897 px: up
 * to a window of about 766 px a table takes the phone layout, and above that
 * a table whose columns need more than its box (Tools, the people on Teams)
 * keeps them and scrolls sideways inside its box. A higher threshold would
 * change desktop: a 1024 px window with the sidebar expanded gives a box of
 * only 682 px.
 *
 * Full literal classes, so Tailwind generates them.
 */
const NARROW = {
  /** Shown only in the phone layout. */
  only: "@min-[640px]:hidden",
  /** Gone in the phone layout. */
  hidden: "@max-[640px]:hidden",
  /** A cell that stays in the phone layout, empty and without padding. */
  emptyCell: "@max-[640px]:p-0",
  /** A long value, such as a date and time, that may wrap in the phone layout. */
  wrap: "@max-[640px]:whitespace-normal",
  /** Text that may break inside a long word (an email, an id) in the phone layout. */
  anywhere: "@max-[640px]:wrap-anywhere",
  /**
   * Text whose long words (14 letters or more, 7 on either side of the break)
   * may hyphenate in the phone layout, in the page's language
   * ("Kraanbeschik-baarheid"), so a title never widens the table; a shorter
   * word moves to the next line whole.
   */
  hyphens: "@max-[640px]:hyphens-auto @max-[640px]:[hyphenate-limit-chars:14_7_7]",
  /** A row of things in a cell that may wrap in the phone layout. */
  flexWrap: "@max-[640px]:flex-wrap",
  /** In such a row, a thing that goes to a line of its own, last, in the phone layout. */
  ownLine: "@max-[640px]:order-last @max-[640px]:basis-full",
} as const;

/** A wide-only part of a cell, such as a chip that moves under the title on a phone. */
export const WIDE_ONLY = NARROW.hidden;

/** A phone-only part of a cell, such as a legend for chips whose column left. */
export const NARROW_ONLY = NARROW.only;

/** A cell's text that keeps one line in a wide table and may wrap in the phone layout. */
export const NARROW_WRAP = NARROW.wrap;

/**
 * A row of things in a cell (a flex row) that may wrap in the phone layout,
 * and its part that then takes a line of its own under the rest.
 */
export const NARROW_FLEX_WRAP = NARROW.flexWrap;
export const NARROW_OWN_LINE = NARROW.ownLine;

/**
 * What a column does in the phone layout: stays (the default, for the first
 * and the last column), leaves (`"hide"`), or stays empty (`"empty"`), which a
 * table with group rows needs, so every row keeps its number of columns and
 * the group rows' cells stay in place. Give a column's head and cells the same.
 */
export type NarrowColumn = "keep" | "hide" | "empty";

function narrowCellClass(narrow: NarrowColumn): string | undefined {
  return narrow === "hide" ? NARROW.hidden : narrow === "empty" ? NARROW.emptyCell : undefined;
}

/** A cell's content, hidden in the phone layout when its column stays empty there. */
function CellContent({ narrow, children }: { narrow: NarrowColumn; children: ReactNode }) {
  return narrow === "empty" ? (
    <div className={cn("contents", NARROW.hidden)}>{children}</div>
  ) : (
    children
  );
}

/** A row's leading icon: De Ceuster blue, 1 em, level with the title's first line. */
export const TABLE_ICON_CLASS = "text-primary mt-0.5 h-4 w-4 shrink-0";

interface DataTableProps {
  /** The table's name for screen readers, such as its page's or its section's title. */
  label: string;
  /** While the rows on screen make way for others (another page, a filter): they dim. */
  busy?: boolean;
  children: ReactNode;
}

/**
 * The table and its box. The box is a size container, so a column can leave
 * when the table's own room is tight (`@max-3xl:hidden` on its header and
 * cells, or `narrow` for the phone layout), whatever the window and the
 * sidebar do.
 */
export function DataTable({ label, busy = false, children }: DataTableProps) {
  return (
    // `relative` keeps screen-reader labels inside the scroller, so they never widen the page.
    <div className="scrollbar-themed @container relative overflow-x-auto rounded-lg border">
      <table
        className={cn("w-full text-sm transition-opacity", busy && "opacity-60")}
        aria-label={label}
        aria-busy={busy || undefined}
      >
        {children}
      </table>
    </div>
  );
}

/** The header: one row in the light grey tint. */
export function TableHead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="bg-muted/10">{children}</tr>
    </thead>
  );
}

interface TableHeadCellProps {
  align?: Align;
  /** The column's sort, on a column that sorts: said to screen readers. A sortable column takes `<SortableHeadCell>`. */
  sort?: "ascending" | "descending" | "none";
  /** What the column does in the phone layout (`NarrowColumn`); the same as its cells. */
  narrow?: NarrowColumn;
  className?: string;
  children?: ReactNode;
}

/** A column's name. Give an actions column a `sr-only` name and `w-px`, so it takes no room. */
export function TableHeadCell({
  align = "left",
  sort,
  narrow = "keep",
  className,
  children,
}: TableHeadCellProps) {
  return (
    <th
      className={cn("px-3 py-2 font-medium", ALIGN[align], narrowCellClass(narrow), className)}
      aria-sort={sort}
    >
      <CellContent narrow={narrow}>{children}</CellContent>
    </th>
  );
}

interface SortButtonProps {
  /** The column's order now (`ariaSort`): an arrow up or down, or the muted double arrow. */
  sort: AriaSort;
  onSort: () => void;
  /** A right-aligned column puts its arrow before its name, so the names line up on the right. */
  align?: Align;
  /** In the phone layout the arrow may go under the name, so a row of narrow columns fits. */
  wrap?: boolean;
  /**
   * Says the order to screen readers in the button's name, for a button
   * whose own header (which carries `aria-sort`) has left, such as a column's
   * sort shown in the first column's header in the phone layout.
   */
  sayOrder?: boolean;
  children: ReactNode;
}

/**
 * A column's name as a button that sorts the table on it, with an arrow for
 * the sorted column's order. Mostly inside `<SortableHeadCell>`; on its own
 * for a column's sort that stays in the phone layout after its column left.
 */
export function SortButton({
  sort,
  onSort,
  align = "left",
  wrap = false,
  sayOrder = false,
  children,
}: SortButtonProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onSort}
      className={cn(
        // Centred lines matter only once the arrow wraps under the name (`wrap`).
        "hover:text-primary focus-visible:ring-ring tap-target inline-flex items-center justify-center gap-1 rounded-sm font-medium focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative",
        align === "right" && "flex-row-reverse",
        wrap && NARROW.flexWrap,
      )}
    >
      {children}
      {sort && sayOrder && (
        <span className="sr-only">
          {", "}
          {sort === "ascending" ? t("table.sortedAscending") : t("table.sortedDescending")}
        </span>
      )}
      {sort === "ascending" ? (
        <ArrowUp aria-hidden="true" className="h-3 w-3" />
      ) : sort === "descending" ? (
        <ArrowDown aria-hidden="true" className="h-3 w-3" />
      ) : (
        <ChevronsUpDown aria-hidden="true" className="text-muted h-3 w-3" />
      )}
    </button>
  );
}

interface SortableHeadCellProps extends Omit<SortButtonProps, "sayOrder"> {
  /** What the column does in the phone layout (`NarrowColumn`); the same as its cells. */
  narrow?: NarrowColumn;
  /** How the column sorts, in a tooltip on its button, where the order is not obvious. */
  hint?: ReactNode;
  /** A wrapper round the button, such as a class that hides it while its column is out. */
  contentClassName?: string;
  /** More of the header after the button, such as another column's sort in the phone layout. */
  after?: ReactNode;
  className?: string;
}

/**
 * A column's header that sorts the table on it: its name as a button, an
 * arrow for the sorted column's order (`aria-sort` on the header), Enter and
 * Space by keyboard. Give it the props of `useTableSort`'s `head`:
 *
 *     const sort = useTableSort({ columns: { name: "asc", calls: "desc" }, initial: null });
 *     <TableHead>
 *       <SortableHeadCell {...sort.head("name")}>Naam</SortableHeadCell>
 *       <SortableHeadCell {...sort.head("calls")} align="right">Oproepen</SortableHeadCell>
 *     </TableHead>
 *
 * The header row gets room for the button's 44 px tap area on a touch screen.
 */
export function SortableHeadCell({
  sort,
  onSort,
  align = "left",
  wrap,
  narrow,
  hint,
  contentClassName,
  after,
  className,
  children,
}: SortableHeadCellProps) {
  const button = (
    <SortButton sort={sort} onSort={onSort} align={align} wrap={wrap}>
      {children}
    </SortButton>
  );
  const control = hint ? (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent className="text-left font-normal">{hint}</TooltipContent>
    </Tooltip>
  ) : (
    button
  );
  return (
    <TableHeadCell
      align={align}
      narrow={narrow}
      sort={sort}
      className={cn("whitespace-nowrap pointer-coarse:py-2.5", className)}
    >
      {contentClassName ? <span className={contentClassName}>{control}</span> : control}
      {after}
    </TableHeadCell>
  );
}

interface TableGroupRowProps {
  title: ReactNode;
  /** A muted line after the title: what the group holds. */
  summary?: ReactNode;
  /** The table's number of columns. */
  colSpan: number;
  /**
   * A cell in the last column, such as a switch for the whole group. When it
   * is given (even `false`), the title spans one column less.
   */
  trailing?: ReactNode;
  /**
   * A group that folds: its title becomes a button with a chevron that shows
   * or hides the rows under it (`aria-expanded`). Leave out the rows of a
   * folded group; the row itself stays.
   */
  collapse?: {
    expanded: boolean;
    onToggle: () => void;
  };
  /**
   * Figures on the right of the title's cell, muted, such as "3 items, 1,5
   * dag": they stay in the row's one cell, so the row spans the whole table
   * in the phone layout too.
   */
  aside?: ReactNode;
  /**
   * `tint` (the default): De Ceuster's light blue tint. `brand`: solid De
   * Ceuster blue with white text, for a group that must stand out from the
   * others, such as the triage a developer reviews.
   */
  tone?: "tint" | "brand";
}

/** A group row's colours per tone: the row, its title, its muted texts, and the toggle. */
const GROUP_TONE = {
  tint: {
    row: "bg-accent-light",
    title: "text-foreground",
    muted: "text-muted",
    toggle: "hover:text-primary focus-visible:ring-ring",
  },
  brand: {
    row: "bg-primary text-primary-foreground",
    title: "text-primary-foreground",
    muted: "text-primary-foreground",
    toggle: "hover:underline focus-visible:ring-primary-foreground underline-offset-2",
  },
} as const;

/**
 * A group's header row in De Ceuster's light blue tint (or, with `tone`
 * `brand`, in solid De Ceuster blue), which names the rows under it
 * (`scope="rowgroup"`): put it first in the group's `<tbody>`.
 */
export function TableGroupRow({
  title,
  summary,
  colSpan,
  trailing,
  collapse,
  aside,
  tone = "tint",
}: TableGroupRowProps) {
  const hasTrailing = trailing !== undefined;
  const colours = GROUP_TONE[tone];
  const heading = (
    <>
      {collapse ? (
        <button
          type="button"
          aria-expanded={collapse.expanded}
          onClick={collapse.onToggle}
          className={cn(
            "tap-target -ml-1 inline-flex items-center gap-1 rounded-sm px-1 focus-visible:ring-2 focus-visible:outline-none pointer-coarse:relative",
            colours.toggle,
          )}
        >
          <ChevronRight
            aria-hidden="true"
            className={cn(
              "size-4 shrink-0 transition-transform",
              colours.muted,
              collapse.expanded && "rotate-90",
            )}
          />
          <span className={cn(colours.title, "font-semibold")}>{title}</span>
        </button>
      ) : (
        <span className={cn(colours.title, "font-semibold")}>{title}</span>
      )}
      {summary && <span className={cn("ml-2 text-xs", colours.muted)}>{summary}</span>}
    </>
  );
  return (
    <tr className={cn("border-t", colours.row)} data-tone={tone === "tint" ? undefined : tone}>
      <th
        scope="rowgroup"
        colSpan={hasTrailing ? colSpan - 1 : colSpan}
        className="px-3 py-2 text-left font-normal"
      >
        {aside === undefined ? (
          heading
        ) : (
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <div className="min-w-0">{heading}</div>
            <span className={cn("text-xs whitespace-nowrap tabular-nums", colours.muted)}>
              {aside}
            </span>
          </div>
        )}
      </th>
      {hasTrailing && <td className="px-3 py-2 text-right">{trailing}</td>}
    </tr>
  );
}

interface TableRowProps {
  className?: string;
  children: ReactNode;
}

/** A row, with a line above it. */
export function TableRow({ className, children }: TableRowProps) {
  return <tr className={cn("border-t", className)}>{children}</tr>;
}

interface TableCellProps {
  align?: Align;
  colSpan?: number;
  /** What the column does in the phone layout (`NarrowColumn`); the same as its head. */
  narrow?: NarrowColumn;
  className?: string;
  children?: ReactNode;
}

/** A cell, densely padded. An actions cell takes `px-1 py-1` to keep the row low. */
export function TableCell({
  align = "left",
  colSpan,
  narrow = "keep",
  className,
  children,
}: TableCellProps) {
  return (
    <td
      colSpan={colSpan}
      className={cn(
        "px-3 py-2",
        align !== "left" && ALIGN[align],
        narrowCellClass(narrow),
        className,
      )}
    >
      <CellContent narrow={narrow}>{children}</CellContent>
    </td>
  );
}

interface TableItemProps {
  /** A small line icon, decorative (`aria-hidden`), with `TABLE_ICON_CLASS`. */
  icon?: ReactNode;
  /** What the row is: a tool's title, a person's name. May end in a chip. */
  title: ReactNode;
  /** A muted line under the title: what it does, an email. A long word in it breaks anywhere in the phone layout. */
  summary?: ReactNode;
  /**
   * In the phone layout only, under the summary: what the columns that left
   * said, such as "46 oproepen, 1 fout" (`factLine`) or a row of chips.
   */
  facts?: ReactNode;
  /** Greyed out (off, blocked, unused): the title in the muted colour, still readable. Fade the icon yourself. */
  muted?: boolean;
  /** Under the summary and the facts, such as a failure. */
  children?: ReactNode;
}

/** The first cell's content: the icon, the title and its summary line. */
export function TableItem({
  icon,
  title,
  summary,
  facts,
  muted = false,
  children,
}: TableItemProps) {
  return (
    <div className="flex items-start gap-2.5">
      {icon}
      <div className="min-w-0">
        <p className={cn("font-medium", muted ? "text-muted" : "text-foreground", NARROW.hyphens)}>
          {title}
        </p>
        {/* Breaks anywhere, never hyphenated: it often holds an email or an id, where a hyphen would read as part of it. */}
        {summary && <p className={cn("text-muted text-xs", NARROW.anywhere)}>{summary}</p>}
        {facts && (
          <div
            className={cn(
              "mt-1 space-y-1.5 text-xs",
              muted ? "text-muted" : "text-foreground",
              NARROW.only,
            )}
          >
            {facts}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * One line of facts under a row's title in the phone layout, such as "46
 * oproepen, 1 fout" (`factLine`). A long email or id in it breaks anywhere,
 * so it never widens the table.
 */
export function TableFacts({ children }: { children: ReactNode }) {
  return <p className="wrap-anywhere">{children}</p>;
}

/** A state in words in the state column: "Uit", "Altijd aan", "OK". */
export function StateText({ className, children }: { className?: string; children: ReactNode }) {
  return <span className={cn("text-muted text-xs whitespace-nowrap", className)}>{children}</span>;
}

interface TableEmptyRowProps {
  /** The table's number of columns. */
  colSpan: number;
  /** "Geen tools gevonden.", "Niemand gevonden." */
  children: ReactNode;
  /** Clears the filters; offered when they are what leaves the table empty. */
  onReset?: () => void;
}

/** The one row of a table with nothing to show, with a way to clear the filters. */
export function TableEmptyRow({ colSpan, children, onReset }: TableEmptyRowProps) {
  const { t } = useTranslation();
  return (
    <tr className="border-t">
      <td colSpan={colSpan} className="text-muted px-3 py-8 text-center">
        {children}{" "}
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="text-accent font-medium hover:underline"
          >
            {t("common.resetFilters")}
          </button>
        )}
      </td>
    </tr>
  );
}
